import { Box, CircularProgress, CssBaseline } from "@mui/material";
import DashboardEditContext from "DashboardEditContext";
import { BackendPermissionsType } from "misc/permission.types";
import PublicDataContext from "PublicDataContext";
import { lazy, ReactNode, Suspense, useEffect } from "react";
import { useAuthState, usePermissions, useSidebarState } from "react-admin";
import { useLocation } from "react-router-dom";
import GlobalLoader from "shared/GlobalLoader";

import BackendDataContext from "../BackendDataContext";
import ErrorBoundary from "./ErrorBoundary";
import PortalConsentCheck from "./PortalConsentCheck";

type EdmLayoutPropsType = {
  children: ReactNode;
};

const StaffBaseLayout = lazy(() => import("./StaffBaseLayout"));
const PublicBaseLayout = lazy(() => import("./PublicBaseLayout"));

export default function EdmLayout(props: EdmLayoutPropsType) {
  const { children } = props;
  const { permissions } = usePermissions<BackendPermissionsType>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSidebarIsOpen] = useSidebarState();
  const location = useLocation();
  const { authenticated } = useAuthState();

  const isPublicLocation = (path: string) =>
    [
      "/feedback/",
      "/consent_confirmation/",
      "/order_item_approval/",
      // Legacy approval links, still in approvers' inboxes.
      "/enrollment_approval/",
      "/logout/",
    ].some((prefix) => path.startsWith(prefix));

  const isStaff = permissions && permissions.isStaff;

  useEffect(() => {
    setSidebarIsOpen(true);
  }, []);

  if (isStaff && !isPublicLocation(location.pathname)) {
    return (
      <Suspense
        fallback={
          <Box display="flex" justifyContent="center" alignItems="center" width="100vw" height="100vh">
            <CircularProgress />
          </Box>
        }
      >
        <ErrorBoundary>
          <DashboardEditContext>
            <BackendDataContext>
              <CssBaseline />
              <GlobalLoader />
              <StaffBaseLayout>{children}</StaffBaseLayout>
            </BackendDataContext>
          </DashboardEditContext>
        </ErrorBoundary>
      </Suspense>
    );
  }

  return (
    <Suspense
      fallback={
        <Box display="flex" justifyContent="center" alignItems="center" width="100vw" height="100vh">
          <CircularProgress />
        </Box>
      }
    >
      <ErrorBoundary>
        <PublicDataContext>
          <PublicBaseLayout>
            {isPublicLocation(location.pathname) || !authenticated ? (
              children
            ) : (
              <PortalConsentCheck>{children}</PortalConsentCheck>
            )}
          </PublicBaseLayout>
        </PublicDataContext>
      </ErrorBoundary>
    </Suspense>
  );
}
